import { Avatar, Box, Button, Chip, Grid, Typography } from "@mui/material";
import { BOOKING_STATUS, BOOKING_TYPES } from "constants/general";
import { ROUTES } from "constants/routes";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { updateBookingStatus } from "slices/bookingReducer";
import { updateSelectedBookingData } from "slices/rentalsReducer";
import { useAppDispatch, useAppSelector } from "store/appHooks";
import { convertPrice } from "utils/currency.util";
import { getConversationId } from "utils/messages.util";
import { getUTCYMD } from "utils/timestamp.util";

const PendingBookings = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { dashboardReducer } = useAppSelector((state) => state);
  const { pendingRequests, pendingRequestsCount } = dashboardReducer;

  const updateStatus = (status: number, id: any) =>
    dispatch(
      updateBookingStatus({
        data: {
          id: id,
          status: status,
        },
        onSucess: () => {
          navigate(
            `${ROUTES.RENTALS}?type=${
              status === BOOKING_STATUS.CONFIRMED
                ? "confirmed"
                : status === BOOKING_STATUS.REJECTED
                ? "cancelled"
                : "pending"
            }&booking=${id}`
          );
        },
      })
    );

  return (
    <Box
      sx={{
        padding: "15px",
        boxShadow: "0px 3px 6px 0px #25334B1A, 0px 10px 20px 0px #25334B1A",
        borderRadius: "10px",
      }}
    >
      <Box
        sx={{ mt: "10px", display: "flex", justifyContent: "space-between" }}
      >
        <Typography
          sx={{
            color: "#323232",
            fontSize: "18px",
            fontWeight: 600,
            lineHeight: "26px",
          }}
        >
          Pending {`${pendingRequestsCount <= 1 ? `Request` : `Requests`}`}(
          {pendingRequestsCount})
        </Typography>

        {pendingRequests.length > 0 && (
          <Typography
            component="a"
            sx={{
              color: "#307AFF",
              fontSize: "15px",
              fontWeight: 600,
              textDecoration: "underline",
              lineHeight: "30px",
              cursor: "pointer",
            }}
            onClick={() => navigate(ROUTES.RENTAL_HISTORY)}
          >
            View All
          </Typography>
        )}
      </Box>

      <Box sx={{ mt: "15px" }}>
        {pendingRequests.length === 0 && (
          <Box sx={{ my: "40px" }}>
            <Typography>No Booking found.</Typography>
          </Box>
        )}
        <Grid container spacing={4}>
          {pendingRequests.map((item: any) => (
            <Grid item sm={12} xs={12} md={4} lg={4} xl={4}>
              <Box
                sx={{
                  border: "1px solid #DFDFDF",
                  padding: "20px",
                  borderRadius: "10px",
                }}
              >
                <Box
                  sx={{
                    borderBottom: "1px solid #E2E2E2",
                    pb: "15px",
                    display: "flex",
                    gap: "10px",
                  }}
                  onClick={() =>
                    navigate(
                      `${ROUTES.RENTALS}?booking=${item.id}&type=pending`
                    )
                  }
                >
                  <Avatar
                    src={item?.user?.image_url}
                    sx={{ width: "56px", height: "56px" }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flex: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: {
                            md: "18px",
                            lg: "18px",
                            xl: "18px",
                            sm: "15px",
                            xs: "15px",
                          },
                          lineHeight: "20px",
                        }}
                      >
                        {item?.user?.name}
                      </Typography>
                      <Chip
                        label="Pending"
                        sx={{
                          background: "#FEF6E1",
                          color: "#F1B724",
                          fontWeight: 500,
                          fontSize: "13px",
                          lineHeight: "15px",
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: "400",
                        color: "#323232",
                      }}
                    >
                      Order Date -{" "}
                      {moment(item?.created_at).format("MMM DD on hh:mm a")}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ my: "15px" }}>
                  <Typography
                    sx={{
                      color: "#323232",
                      fontWeight: 600,
                      fontSize: "18px",
                      lineHeight: "26px",

                      height: "48px",
                    }}
                  >
                    {item?.booking_items?.[0]?.listings?.listing_name}
                    &nbsp;&nbsp;
                    {item?.booking_items.length > 1 && (
                      <Typography
                        component="span"
                        sx={{
                          color: "#307AFF",
                          fontSize: "18px",
                          fontWeight: 600,
                        }}
                      >
                        +{item?.booking_items.length - 1} more
                      </Typography>
                    )}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "15px",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography
                        sx={{
                          color: "#A2A2A2",
                          fontSize: "15px",
                          fontWeight: 400,
                          lineHeight: "28px",
                        }}
                      >
                        Order ID -
                      </Typography>
                      <Typography
                        sx={{
                          color: "#323232",
                          fontWeight: 500,
                          fontSize: "15px",
                          lineHeight: "28px",
                        }}
                      >
                        #{item.id}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography
                        sx={{
                          color: "#A2A2A2",
                          fontSize: "15px",
                          fontWeight: 400,
                          lineHeight: "28px",
                        }}
                      >
                        Booking For -
                      </Typography>
                      <Typography
                        sx={{
                          color: "#323232",
                          fontWeight: 500,
                          fontSize: "15px",
                          lineHeight: "28px",
                        }}
                      >
                        {`${moment(getUTCYMD(item.from_date)).format(
                          "MMM DD"
                        )} - ${moment(getUTCYMD(item.to_date)).format(
                          "MMM DD"
                        )}`}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography
                        sx={{
                          color: "#A2A2A2",
                          fontSize: "15px",
                          fontWeight: 400,
                          lineHeight: "28px",
                        }}
                      >
                        Order Amount -
                      </Typography>
                      <Typography
                        sx={{
                          color: "#323232",
                          fontWeight: 500,
                          fontSize: "15px",
                          lineHeight: "28px",
                        }}
                      >
                        {convertPrice(item?.booking_payments?.[0]?.total)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", gap: "20px" }}>
                  <Button
                    variant="outlined"
                    sx={{ padding: 0, height: "56px", borderRadius: "10px" }}
                    fullWidth
                    onClick={() =>
                      updateStatus(BOOKING_STATUS.REJECTED, item.id)
                    }
                  >
                    Decline
                  </Button>
                  <Button
                    variant="contained"
                    sx={{ padding: 0, height: "56px", borderRadius: "10px" }}
                    fullWidth
                    onClick={() =>
                      updateStatus(
                        item.booking_type === BOOKING_TYPES.MANUAL
                          ? BOOKING_STATUS.PAYMENT_PENDING
                          : BOOKING_STATUS.CONFIRMED,
                        item.id
                      )
                    }
                  >
                    Approve
                  </Button>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default PendingBookings;
